/**
 * Add Liability Panel — slide-over form for manually adding a new liability,
 * or for converting an existing security interest into one.
 *
 * When `convertFromSecurityInterestId` is set, the submit handler calls
 * `convertSecurityInterestToLiability` (which inserts the new liability and
 * soft-deletes the source SI) instead of `addManualLiability`.
 */

import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/ui/composed/overlays/sheet";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { ScrollArea } from "@/ui/primitives/scroll-area";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import {
  RANKING_OPTIONS,
  creditorRanking,
  rankingToFlags,
  type CreditorRanking,
} from "./ranking";

const NATURE_OPTIONS = [
  { value: "trade", label: "Trade" },
  { value: "lease", label: "Lease" },
  { value: "tax", label: "Tax" },
  { value: "utility", label: "Utility" },
  { value: "professional_fees", label: "Professional Fees" },
  { value: "loan", label: "Loan" },
  { value: "insurer", label: "Insurer" },
  { value: "other", label: "Other" },
] as const;

const SOURCE_OPTIONS = [
  { value: "aged_payables", label: "Aged Payables" },
  { value: "invoice", label: "Invoice" },
  { value: "contract", label: "Contract" },
  { value: "ledger", label: "Ledger" },
  { value: "bank_statement", label: "Bank Statement" },
  { value: "director_disclosure", label: "Director Disclosure" },
  { value: "proof_of_debt", label: "Proof of Debt" },
  { value: "other", label: "Other" },
] as const;

const CONFIDENCE_OPTIONS = [
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
] as const;

export interface AddLiabilityPrefill {
  creditorName?: string;
  amount?: number;
  natureOfDebt?: string;
  source?: string;
  confidence?: string;
  secured?: boolean;
  preferential?: boolean;
  securityDetails?: string;
  date?: string;
  notes?: string;
}

interface AddLiabilityPanelProps {
  jobId: Id<"jobs">;
  taskId: Id<"summarizationTasks">;
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
  /** When set, submit converts this SI into the new liability (atomic). */
  convertFromSecurityInterestId?: Id<"securityInterestEntries">;
  /** Pre-fill values from a converting SI (or any other source). */
  prefill?: AddLiabilityPrefill;
}

export function AddLiabilityPanel({
  jobId,
  taskId,
  open,
  onClose,
  onCreated,
  convertFromSecurityInterestId,
  prefill,
}: AddLiabilityPanelProps) {
  const [creditorName, setCreditorName] = useState("");
  const [amount, setAmount] = useState("");
  const [natureOfDebt, setNatureOfDebt] = useState("trade");
  const [source, setSource] = useState("director_disclosure");
  const [confidence, setConfidence] = useState("medium");
  const [ranking, setRanking] = useState<CreditorRanking>("unsecured");
  const [securityDetails, setSecurityDetails] = useState("");
  const secured = ranking === "secured";
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");

  const addLiability = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.liabilities.publicMutations
        .addManualLiability,
    ),
  });
  const convertSI = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.securityInterests.publicMutations
        .convertSecurityInterestToLiability,
    ),
  });

  const isConverting = convertFromSecurityInterestId != null;
  const isPending = isConverting ? convertSI.isPending : addLiability.isPending;

  function resetForm() {
    setCreditorName("");
    setAmount("");
    setNatureOfDebt("trade");
    setSource("director_disclosure");
    setConfidence("medium");
    setRanking("unsecured");
    setSecurityDetails("");
    setDate("");
    setNotes("");
  }

  // Hydrate from prefill whenever the sheet opens (false -> true). We
  // intentionally key off `open` only so subsequent parent re-renders (which
  // create new `prefill` object references) don't clobber the user's mid-form
  // edits. The latest `prefill` value is read via the ref captured below.
  const prefillRef = useRef(prefill);
  prefillRef.current = prefill;
  useEffect(() => {
    if (!open) return;
    const p = prefillRef.current;
    setCreditorName(p?.creditorName ?? "");
    setAmount(p?.amount != null ? String(p.amount) : "");
    setNatureOfDebt(p?.natureOfDebt ?? "trade");
    setSource(p?.source ?? "director_disclosure");
    setConfidence(p?.confidence ?? "medium");
    setRanking(
      creditorRanking({
        secured: p?.secured,
        preferential: p?.preferential,
      }),
    );
    setSecurityDetails(p?.securityDetails ?? "");
    setDate(p?.date ?? "");
    setNotes(p?.notes ?? "");
  }, [open]);

  async function handleSubmit() {
    if (!creditorName.trim()) return;

    const sharedArgs = {
      creditorName: creditorName.trim(),
      natureOfDebt: natureOfDebt as
        | "trade"
        | "lease"
        | "tax"
        | "utility"
        | "professional_fees"
        | "loan"
        | "insurer"
        | "other",
      source: source as
        | "aged_payables"
        | "invoice"
        | "contract"
        | "ledger"
        | "bank_statement"
        | "director_disclosure"
        | "proof_of_debt"
        | "other",
      confidence: confidence as "high" | "medium" | "low",
      ...rankingToFlags(ranking),
      securityDetails:
        secured && securityDetails.trim() ? securityDetails.trim() : undefined,
      date: date || undefined,
      notes: notes.trim() || undefined,
    };

    // Convert mutation requires a numeric amount (defaults to 0 if blank).
    const numericAmount = amount ? parseFloat(amount) : 0;
    const result: string = isConverting
      ? ((await convertSI.mutateAsync({
          entryId: convertFromSecurityInterestId,
          amount: Number.isFinite(numericAmount) ? numericAmount : 0,
          ...sharedArgs,
        })) as unknown as string)
      : ((await addLiability.mutateAsync({
          jobId,
          taskId,
          amount: amount ? parseFloat(amount) : undefined,
          ...sharedArgs,
        })) as unknown as string);

    resetForm();
    onCreated(result);
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          resetForm();
          onClose();
        }
      }}
    >
      <SheetContent className="flex w-full flex-col overflow-hidden p-0 sm:max-w-md">
        <div className="px-6 pb-4 pt-6">
          <SheetHeader>
            <SheetTitle className="text-lg font-bold">
              {isConverting ? "Convert to Creditor" : "Add Creditor"}
            </SheetTitle>
            <SheetDescription>
              {isConverting
                ? "Confirm the creditor details. The original security interest will be removed on save."
                : "Manually add a creditor to the summary."}
            </SheetDescription>
          </SheetHeader>
        </div>

        <ScrollArea className="min-h-0 flex-1">
          <div className="space-y-4 px-6 pb-6">
            {/* Creditor Name */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Creditor Name *
              </label>
              <Input
                value={creditorName}
                onChange={(e) => setCreditorName(e.target.value)}
                placeholder="Name of creditor"
                className="h-9 text-sm"
              />
            </div>

            {/* Amount */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Amount
              </label>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="h-9 text-sm tabular-nums"
              />
            </div>

            {/* Type of Debt */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Type of Debt
              </label>
              <Select value={natureOfDebt} onValueChange={setNatureOfDebt}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NATURE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Source */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Source
              </label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Confidence */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Confidence
              </label>
              <Select value={confidence} onValueChange={setConfidence}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONFIDENCE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Ranking */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Ranking
              </label>
              <Select
                value={ranking}
                onValueChange={(value) => setRanking(value as CreditorRanking)}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RANKING_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {ranking === "preferential" && (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Employee entitlements are captured in the Employees register.
                  Recording them here as well would count them twice.
                </p>
              )}
            </div>

            {/* Security Details (conditional) */}
            {secured && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Security Details
                </label>
                <Input
                  value={securityDetails}
                  onChange={(e) => setSecurityDetails(e.target.value)}
                  placeholder="Describe the security interest"
                  className="h-9 text-sm"
                />
              </div>
            )}

            {/* Date */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                As at Date (optional)
              </label>
              <DatePicker value={date} onChange={setDate} />
            </div>

            {/* Notes */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Notes
              </label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any relevant details..."
                rows={3}
                className="text-sm"
              />
            </div>

            {/* Submit */}
            <Button
              className="w-full"
              disabled={!creditorName.trim() || isPending}
              onClick={handleSubmit}
            >
              {isPending
                ? isConverting
                  ? "Converting..."
                  : "Adding..."
                : isConverting
                  ? "Convert"
                  : "Add Creditor"}
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
